import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { colors, spacing, shadows } from '../theme/index';
import API_BASE_URL from '../config/api';
import FinanceInvoiceModal from './FinanceInvoiceModal';
import FinancePartyModal from './FinancePartyModal';
import FinancePartyFormModal from './FinancePartyFormModal';
import FinancePaymentModal from './FinancePaymentModal';
import {
  INVOICE_SORT_OPTIONS,
  MODULES,
  PAGE_SIZE_OPTIONS,
  PARTY_SORT_OPTIONS,
  STATUS_FILTER_OPTIONS,
  TONES,
  buildQueryString,
  formatCurrency,
  formatDate,
  getBalance,
  getDueInfo,
  getPaidAmount,
  getStatusBadge,
  hasActiveFilters,
} from './financeUtils';

const EMPTY_FILTERS = {
  search: '',
  invoiceNumber: '',
  partySearch: '',
  status: 'all',
  partyId: '',
  from: '',
  to: '',
  sort: 'newest',
};

const EMPTY_PAGINATION = { page: 1, limit: 25, total: 0, totalPages: 1 };

// Small shared pieces ---------------------------------------------------
const Badge = ({ tone, children }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '3px 10px',
      borderRadius: '999px',
      fontSize: '0.68rem',
      fontWeight: 800,
      letterSpacing: '0.05em',
      backgroundColor: TONES[tone].background,
      color: TONES[tone].color,
      border: '1px solid ' + TONES[tone].border,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

const ActionButton = ({ onClick, children, tone, disabled, title }) => {
  const palette = tone ? TONES[tone] : null;
  return (
    <button
      type='button'
      title={title || undefined}
      disabled={disabled}
      onClick={onClick}
      style={{
        padding: spacing.xs + ' ' + spacing.sm,
        borderRadius: '0.375rem',
        border: '1px solid ' + (palette ? palette.border : colors.border),
        backgroundColor: palette ? palette.background : 'white',
        color: palette ? palette.color : colors.text.primary,
        fontWeight: 700,
        fontSize: '0.75rem',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
};

const EmptyState = ({ title, hint }) => (
  <div style={{ textAlign: 'center', padding: spacing['2xl'] + ' ' + spacing.lg, color: colors.text.secondary }}>
    <p style={{ fontSize: '2rem', margin: 0 }}>📄</p>
    <p style={{ fontSize: '1.05rem', fontWeight: 700, color: colors.text.primary, marginTop: spacing.sm }}>{title}</p>
    {hint ? <p style={{ fontSize: '0.9rem', marginTop: spacing.xs }}>{hint}</p> : null}
  </div>
);

const toolbarInputStyle = {
  padding: spacing.sm + ' ' + spacing.md,
  border: '1px solid ' + colors.border,
  borderRadius: '0.5rem',
  fontSize: '0.85rem',
  backgroundColor: 'white',
  color: colors.text.primary,
  fontFamily: 'inherit',
  boxSizing: 'border-box',
  width: '100%',
  minWidth: 0,
};

const clearFiltersStyle = {
  padding: spacing.sm + ' ' + spacing.md,
  borderRadius: '0.5rem',
  border: '1px solid ' + colors.border,
  backgroundColor: colors.surface,
  color: colors.text.secondary,
  fontWeight: 700,
  fontSize: '0.82rem',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

const pagerButtonStyle = {
  padding: spacing.sm + ' ' + spacing.md,
  borderRadius: '0.5rem',
  border: '1px solid ' + colors.border,
  backgroundColor: 'white',
  color: colors.text.primary,
  fontWeight: 700,
  fontSize: '0.82rem',
  cursor: 'pointer',
};

// Renders one finance module:
//   moduleKey="purchases"     -> invoices received from distributors (money we owe)
//   moduleKey="distributions" -> sales billed to stores (money stores owe us)
// The parent admin section decides which module to show, so each module lives in
// its own tab (Purchases / Sales) while sharing all of this code.
function PurchasesDistribution({ moduleKey = 'purchases' }) {
  const [view, setView] = useState('invoices');

  // Invoice list state
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [invoices, setInvoices] = useState([]);
  const [pagination, setPagination] = useState({ ...EMPTY_PAGINATION });
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Party (distributor / store) state
  const [parties, setParties] = useState([]);
  const [partySummaries, setPartySummaries] = useState([]);
  const [partySearch, setPartySearch] = useState('');
  const [partySort, setPartySort] = useState('name_asc');
  const [partyPage, setPartyPage] = useState(1);
  const [partyLimit, setPartyLimit] = useState(10);
  const [partyPagination, setPartyPagination] = useState({ ...EMPTY_PAGINATION, limit: 10 });
  const [partiesLoading, setPartiesLoading] = useState(false);

  // UI state
  const [toast, setToast] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [paymentModal, setPaymentModal] = useState(null);
  const [partyDetailId, setPartyDetailId] = useState(null);
  const [partyFormModal, setPartyFormModal] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [actionInvoiceId, setActionInvoiceId] = useState(null);
  const [actionPartyId, setActionPartyId] = useState(null);

  const toastTimer = useRef(null);
  const invoicesRequest = useRef(0);
  const summaryRequest = useRef(0);
  const partiesRequest = useRef(0);
  const partySummaryRequest = useRef(0);

  const config = MODULES[moduleKey];
  // "Cash Bills" view inside the Purchases module (bills paid at purchase time).
  const isCashView = Boolean(config.hasCashBills) && view === 'cash';

  const notify = useCallback((message, type) => {
    if (!message) return;
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type: type === 'error' ? 'error' : 'success' });
    toastTimer.current = setTimeout(() => setToast(null), 4500);
  }, []);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const apiRequest = useCallback(async (path, options) => {
    const response = await fetch(API_BASE_URL + path, {
      method: (options && options.method) || 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + localStorage.getItem('token'),
      },
      body: options && options.body ? JSON.stringify(options.body) : undefined,
    });
    const body = await response.json().catch(() => ({}));
    return { ok: response.ok, status: response.status, body };
  }, []);

  // ---- data loading ----------------------------------------------------
  // The selected distributor/store has to be sent with the field name the API
  // expects (distributorId / storeId) - "partyId" is only the form-state key.
  const buildInvoiceQuery = useCallback(
    (extra) => {
      const params = { ...filters, ...(extra || {}) };
      if (isCashView) {
        params.paymentMethod = 'cash';
      }
      if (params.partyId) {
        params[MODULES[moduleKey].partyIdField] = params.partyId;
      }
      delete params.partyId;
      return buildQueryString(params);
    },
    [filters, moduleKey, isCashView]
  );

  const loadInvoices = useCallback(async () => {
    const requestId = ++invoicesRequest.current;
    setInvoicesLoading(true);
    const query = buildInvoiceQuery({ page, limit });
    const result = await apiRequest('/api/admin/finance/' + MODULES[moduleKey].endpoint + query);
    if (requestId !== invoicesRequest.current) return;
    if (result.ok) {
      setInvoices(Array.isArray(result.body.data) ? result.body.data : []);
      setPagination(result.body.pagination || { ...EMPTY_PAGINATION, limit });
      setListError('');
    } else {
      setInvoices([]);
      setListError(result.body.message || 'Could not load invoices. Please sign in again and retry.');
    }
    setInvoicesLoading(false);
  }, [apiRequest, buildInvoiceQuery, page, limit, moduleKey]);

  const loadSummary = useCallback(async () => {
    const requestId = ++summaryRequest.current;
    setSummaryLoading(true);
    const query = buildInvoiceQuery();
    const result = await apiRequest('/api/admin/finance/' + MODULES[moduleKey].endpoint + '/summary' + query);
    if (requestId !== summaryRequest.current) return;
    setSummary(result.ok ? result.body.data : null);
    setSummaryLoading(false);
  }, [apiRequest, buildInvoiceQuery, moduleKey]);

  const loadParties = useCallback(async () => {
    const result = await apiRequest('/api/admin/finance/' + MODULES[moduleKey].partyEndpoint);
    setParties(result.ok && Array.isArray(result.body.data) ? result.body.data : []);
  }, [apiRequest, moduleKey]);

  const loadPartySummaries = useCallback(async () => {
    const requestId = ++partySummaryRequest.current;
    setPartiesLoading(true);
    const query = buildQueryString({ search: partySearch, sort: partySort, page: partyPage, limit: partyLimit });
    const result = await apiRequest('/api/admin/finance/' + MODULES[moduleKey].partyEndpoint + '/summary' + query);
    if (requestId !== partySummaryRequest.current) return;
    if (result.ok) {
      setPartySummaries(Array.isArray(result.body.data) ? result.body.data : []);
      setPartyPagination(result.body.pagination || { ...EMPTY_PAGINATION, limit: partyLimit });
    } else {
      setPartySummaries([]);
    }
    setPartiesLoading(false);
  }, [apiRequest, moduleKey, partySearch, partySort, partyPage, partyLimit]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadParties();
  }, [loadParties]);

  useEffect(() => {
    if (view === 'parties') loadPartySummaries();
  }, [view, loadPartySummaries]);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadInvoices(), loadSummary(), loadParties(), loadPartySummaries()]);
    setRefreshToken((value) => value + 1);
  }, [loadInvoices, loadSummary, loadParties, loadPartySummaries]);

  // Reset paging whenever the module or the view changes.
  useEffect(() => {
    setPage(1);
  }, [moduleKey, view, filters.status, filters.partyId, filters.from, filters.to, filters.search, filters.invoiceNumber, filters.partySearch, filters.sort, limit]);

  useEffect(() => {
    setPartyPage(1);
  }, [moduleKey, partySearch, partySort, partyLimit]);

  // ---- actions ---------------------------------------------------------
  const togglePaymentStatus = async (invoice) => {
    const nextStatus = invoice.paymentStatus === 'paid' ? 'unpaid' : 'paid';
    if (nextStatus === 'unpaid' && !invoice.dueDate) {
      notify('This invoice has no due date. Use Edit to set a due date before clearing the payment.', 'error');
      return;
    }

    if (nextStatus === 'unpaid') {
      const confirmed = window.confirm(
        'Clear the recorded payment of ' + formatCurrency(getPaidAmount(invoice)) + ' for invoice ' +
          invoice.invoiceNumber + ' and mark it as unpaid?'
      );
      if (!confirmed) return;
    }

    setActionInvoiceId(invoice.id);
    const result = await apiRequest('/api/admin/finance/' + config.endpoint + '/' + invoice.id + '/payment-status', {
      method: 'PATCH',
      body: { paymentStatus: nextStatus },
    });
    setActionInvoiceId(null);

    if (!result.ok) {
      notify(result.body.message || 'Could not update the payment status', 'error');
      return;
    }
    notify(nextStatus === 'paid' ? 'Marked as Paid' : 'Marked as Unpaid', 'success');
    await reloadAll();
  };

  const deleteInvoice = async (invoice) => {
    const confirmed = window.confirm(
      'Delete invoice ' + invoice.invoiceNumber + ' for ' + invoice.partyName + ' (' + formatCurrency(invoice.amount) + ')?\n\nThis cannot be undone.'
    );
    if (!confirmed) return;

    setActionInvoiceId(invoice.id);
    const result = await apiRequest('/api/admin/finance/' + config.endpoint + '/' + invoice.id, { method: 'DELETE' });
    setActionInvoiceId(null);

    if (!result.ok) {
      notify(result.body.message || 'Could not delete the invoice', 'error');
      return;
    }
    notify(config.invoiceTitle + ' invoice deleted', 'success');
    await reloadAll();
  };

  const deleteParty = async (party) => {
    const confirmed = window.confirm(
      'Delete ' + config.partyTitle.toLowerCase() + ' "' + party.name + '"?\n\nOnly ' +
        config.partyTitle.toLowerCase() + 's without invoices can be deleted.'
    );
    if (!confirmed) return;

    setActionPartyId(party.id);
    const result = await apiRequest('/api/admin/finance/' + config.partyEndpoint + '/' + party.id, { method: 'DELETE' });
    setActionPartyId(null);

    if (!result.ok) {
      notify(result.body.message || 'Could not delete this ' + config.partyTitle.toLowerCase(), 'error');
      return;
    }
    notify(config.partyTitle + ' deleted', 'success');
    await reloadAll();
  };

  const handleInvoiceSaved = async (message) => {
    setInvoiceModal(null);
    notify(message, 'success');
    await reloadAll();
  };

  const handlePaymentSaved = async (message) => {
    setPaymentModal(null);
    notify(message, 'success');
    await reloadAll();
  };

  const handlePartySaved = async (message) => {
    setPartyFormModal(null);
    notify(message, 'success');
    await reloadAll();
  };

  const handlePartyModalChanged = async (message, type) => {
    if (!message) {
      await reloadAll();
      return;
    }
    notify(message, type === 'error' ? 'error' : 'success');
  };

  const updateFilter = (field, value) => {
    setFilters((current) => ({ ...current, [field]: value }));
  };

  const resetFilters = () => {
    setFilters({ ...EMPTY_FILTERS });
  };

  const filtersActive = hasActiveFilters(filters);

  const openAddInvoice = (party) => {
    setInvoiceModal({
      invoice: null,
      presetParty: party || null,
      presetBillType: isCashView ? 'cash' : null,
    });
  };

  // ---- render helpers --------------------------------------------------
  const summaryCards = [
    {
      label: isCashView ? config.cashTotalLabel : config.totalLabel,
      value: summary ? summary.totalAmount : 0,
      tone: 'neutral',
      hint: summary
        ? summary.invoiceCount + (summary.invoiceCount === 1 ? ' invoice' : ' invoices') +
          (isCashView ? ' (cash)' : '')
        : '',
    },
    {
      label: config.paidLabel,
      value: summary ? summary.totalPaid : 0,
      tone: 'paid',
      hint: summary ? summary.fullyPaidCount + ' fully paid' : '',
    },
    {
      label: config.outstandingLabel,
      value: summary ? summary.totalOutstanding : 0,
      tone: 'unpaid',
      hint: summary ? summary.balanceCount + ' invoice(s) with balance' : '',
    },
    {
      label: config.overdueLabel,
      value: summary ? summary.overdueAmount : 0,
      tone: 'overdue',
      hint: summary && summary.overdueCount ? summary.overdueCount + ' overdue invoice(s)' : 'Nothing overdue',
    },
    {
      label: config.partialLabel,
      value: summary ? summary.partialAmount : 0,
      tone: 'partial',
      hint: summary ? summary.partialCount + ' partly paid invoice(s)' : '',
    },
  ];

  const showSummaryValue = (value) => (summaryLoading && !summary ? '...' : formatCurrency(value));

  return (
    <div>
      {/* Title + primary actions */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: spacing.md,
          flexWrap: 'wrap',
          marginBottom: spacing.lg,
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800, color: colors.text.primary }}>
            {isCashView ? '💵 ' : (moduleKey === 'purchases' ? '📥 ' : '📤 ')}
            {isCashView ? config.cashTitle : config.title}
          </h2>
          <p style={{ margin: spacing.xs + ' 0 0', color: colors.text.secondary, fontSize: '0.92rem' }}>
            {isCashView ? config.cashSubtitle : config.subtitle}
          </p>
        </div>
        <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap' }}>
          <button
            type='button'
            onClick={() => setPartyFormModal({ party: null })}
            style={{
              padding: spacing.sm + ' ' + spacing.md,
              borderRadius: '0.5rem',
              border: '2px solid ' + colors.primary[500],
              backgroundColor: 'white',
              color: colors.primary[600],
              fontWeight: 800,
              fontSize: '0.88rem',
              cursor: 'pointer',
            }}
          >
            + Add {config.partyTitle}
          </button>
          <button
            type='button'
            onClick={() => openAddInvoice(null)}
            style={{
              padding: spacing.sm + ' ' + spacing.md,
              borderRadius: '0.5rem',
              border: 'none',
              backgroundColor: colors.primary[500],
              color: 'white',
              fontWeight: 800,
              fontSize: '0.88rem',
              cursor: 'pointer',
            }}
          >
            + {isCashView ? config.cashAddLabel : config.addLabel}
          </button>
        </div>
      </div>

      {/* Dashboard summary cards (calculated in the database) */}
      <div className='pd-summary-grid' style={{ marginBottom: spacing.lg }}>
        {summaryCards.map((card) => (
          <div
            key={card.label}
            style={{
              backgroundColor: TONES[card.tone].background,
              border: '1px solid ' + TONES[card.tone].border,
              borderRadius: '0.75rem',
              padding: spacing.md,
            }}
          >
            <p style={{ margin: 0, fontSize: '0.75rem', fontWeight: 800, color: TONES[card.tone].color, letterSpacing: '0.02em' }}>
              {card.label}
            </p>
            <p style={{ margin: spacing.xs + ' 0 0', fontSize: '1.3rem', fontWeight: 900, color: TONES[card.tone].color }}>
              {showSummaryValue(card.value)}
            </p>
            {card.hint ? (
              <p style={{ margin: spacing.xs + ' 0 0', fontSize: '0.72rem', color: colors.text.secondary }}>{card.hint}</p>
            ) : null}
          </div>
        ))}
      </div>

      {/* View switch + filters card */}
      <div
        style={{
          backgroundColor: 'white',
          border: '1px solid ' + colors.border,
          borderRadius: '0.75rem',
          boxShadow: shadows.sm,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: spacing.md,
            flexWrap: 'wrap',
            padding: spacing.md + ' ' + spacing.lg,
            borderBottom: '1px solid ' + colors.border,
          }}
        >
          <div style={{ display: 'flex', gap: spacing.xs, flexWrap: 'wrap' }}>
            {[
              { key: 'invoices', label: 'Invoices' },
              { key: 'parties', label: config.partyTitlePlural },
              ...(config.hasCashBills ? [{ key: 'cash', label: config.cashTabLabel }] : []),
            ].map((tab) => {
              const active = view === tab.key;
              return (
                <button
                  key={tab.key}
                  type='button'
                  onClick={() => setView(tab.key)}
                  style={{
                    padding: spacing.sm + ' ' + spacing.md,
                    borderRadius: '0.5rem',
                    border: '1px solid ' + (active ? colors.primary[500] : colors.border),
                    backgroundColor: active ? colors.primary[50] : 'white',
                    color: active ? colors.primary[700] : colors.text.primary,
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    cursor: 'pointer',
                  }}
                >
                  {tab.label}
                </button>
              );
            })}
          </div>

          {view !== 'parties' ? (
            <div className='pd-toolbar' style={{ flex: 1, minWidth: '260px' }}>
              <input
                type='text'
                value={filters.search}
                onChange={(event) => updateFilter('search', event.target.value)}
                placeholder={'Search invoice or ' + config.partyTitle.toLowerCase()}
                style={toolbarInputStyle}
              />
              <input
                type='text'
                value={filters.invoiceNumber}
                onChange={(event) => updateFilter('invoiceNumber', event.target.value)}
                placeholder='Search invoice number'
                style={toolbarInputStyle}
              />
              <select
                value={filters.status}
                onChange={(event) => updateFilter('status', event.target.value)}
                style={toolbarInputStyle}
              >
                {STATUS_FILTER_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <select
                value={filters.partyId}
                onChange={(event) => updateFilter('partyId', event.target.value)}
                style={toolbarInputStyle}
              >
                <option value=''>{'All ' + config.partyTitlePlural.toLowerCase()}</option>
                {parties.map((party) => (
                  <option key={party.id} value={party.id}>
                    {party.name}
                  </option>
                ))}
              </select>
              <input
                type='date'
                value={filters.from}
                onChange={(event) => updateFilter('from', event.target.value)}
                title={'From ' + config.dateLabel}
                style={toolbarInputStyle}
              />
              <input
                type='date'
                value={filters.to}
                onChange={(event) => updateFilter('to', event.target.value)}
                title={'To ' + config.dateLabel}
                style={toolbarInputStyle}
              />
              <select
                value={filters.sort}
                onChange={(event) => updateFilter('sort', event.target.value)}
                style={toolbarInputStyle}
              >
                {INVOICE_SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {filtersActive ? (
                <button type='button' onClick={resetFilters} style={clearFiltersStyle}>
                  Clear filters
                </button>
              ) : null}
            </div>
          ) : (
            <div className='pd-toolbar' style={{ flex: 1, minWidth: '260px' }}>
              <input
                type='text'
                value={partySearch}
                onChange={(event) => setPartySearch(event.target.value)}
                placeholder={'Search ' + config.partyTitle.toLowerCase() + ' name'}
                style={toolbarInputStyle}
              />
              <select value={partySort} onChange={(event) => setPartySort(event.target.value)} style={toolbarInputStyle}>
                {PARTY_SORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {view !== 'parties' ? (
          <div>
            {listError ? (
              <div
                style={{
                  margin: spacing.lg,
                  padding: spacing.md,
                  borderRadius: '0.5rem',
                  backgroundColor: '#fdeaea',
                  color: '#b42318',
                  fontWeight: 600,
                }}
              >
                {listError}
              </div>
            ) : null}

            {invoicesLoading ? (
              <p style={{ padding: spacing.lg, color: colors.text.secondary }}>Loading invoices...</p>
            ) : invoices.length === 0 ? (
              <EmptyState
                title={listError ? 'Invoices unavailable' : (isCashView ? config.cashEmptyInvoices : config.emptyInvoices)}
                hint={
                  listError
                    ? ''
                    : filtersActive
                      ? 'No invoices match the selected filters.'
                      : (isCashView ? config.cashEmptyInvoicesHint : config.emptyInvoicesHint)
                }
              />
            ) : (
              <div className='pd-table-wrap'>
                <table className='pd-table'>
                  <thead>
                    <tr>
                      <th>{config.partyTitle}</th>
                      <th>Invoice #</th>
                      <th style={{ textAlign: 'right' }}>Amount</th>
                      <th style={{ textAlign: 'right' }}>Paid</th>
                      <th style={{ textAlign: 'right' }}>Balance</th>
                      <th>{config.dateLabel}</th>
                      <th>Status</th>
                      <th>Due Date</th>
                      <th>Due / Overdue</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>

                    {invoices.map((invoice) => {
                      const badge = getStatusBadge(invoice);
                      const due = getDueInfo(invoice);
                      const busy = actionInvoiceId === invoice.id;
                      const invoicePaid = getPaidAmount(invoice);
                      const balance = getBalance(invoice);
                      const isCashBill = invoice.paymentMethod === 'cash';
                      return (
                        <tr key={invoice.id} style={{ backgroundColor: invoice.isOverdue ? '#fffbfb' : 'white' }}>
                          <td>
                            <button
                              type='button'
                              onClick={() => setPartyDetailId(invoice.partyId)}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                color: colors.primary[600],
                                fontWeight: 700,
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontSize: '0.9rem',
                              }}
                            >
                              {invoice.partyName}
                            </button>
                          </td>
                          <td style={{ fontWeight: 600 }}>
                            {invoice.invoiceNumber}
                            {isCashBill ? (
                              <span
                                style={{
                                  marginLeft: '6px',
                                  padding: '2px 8px',
                                  borderRadius: '999px',
                                  fontSize: '0.62rem',
                                  fontWeight: 800,
                                  letterSpacing: '0.05em',
                                  backgroundColor: TONES.paid.background,
                                  color: TONES.paid.color,
                                  border: '1px solid ' + TONES.paid.border,
                                }}
                              >
                                CASH
                              </span>
                            ) : null}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 800 }}>{formatCurrency(invoice.amount)}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700, color: invoicePaid > 0 ? TONES.paid.color : colors.text.tertiary }}>
                            {formatCurrency(invoicePaid)}
                          </td>
                          <td
                            style={{
                              textAlign: 'right',
                              fontWeight: 900,
                              color: balance > 0 ? TONES.unpaid.color : TONES.paid.color,
                            }}
                          >
                            {formatCurrency(balance)}
                          </td>
                          <td>{formatDate(invoice[config.dateField])}</td>
                          <td>
                            <Badge tone={badge.tone}>{badge.label}</Badge>
                          </td>
                          <td>{invoice.dueDate ? formatDate(invoice.dueDate) : '-'}</td>
                          <td style={{ color: TONES[due.tone].color, fontWeight: 700, fontSize: '0.82rem' }}>{due.label}</td>
                          <td style={{ textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {balance > 0 ? (
                                <ActionButton onClick={() => setPaymentModal(invoice)} disabled={busy} tone='partial'>
                                  Record Payment
                                </ActionButton>
                              ) : null}
                              {balance > 0 ? (
                                <ActionButton onClick={() => togglePaymentStatus(invoice)} disabled={busy} tone='paid'>
                                  Mark Paid
                                </ActionButton>
                              ) : null}
                              {invoicePaid > 0 && !isCashBill ? (
                                <ActionButton onClick={() => togglePaymentStatus(invoice)} disabled={busy} tone='unpaid'>
                                  Mark Unpaid
                                </ActionButton>
                              ) : null}
                              <ActionButton onClick={() => setInvoiceModal({ invoice })} disabled={busy}>
                                Edit
                              </ActionButton>
                              <ActionButton onClick={() => deleteInvoice(invoice)} disabled={busy} tone='overdue'>
                                Delete
                              </ActionButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className='pd-pagination'>
              <span style={{ fontSize: '0.82rem', color: colors.text.secondary }}>
                {pagination.total === 0
                  ? 'No records'
                  : 'Page ' + pagination.page + ' of ' + pagination.totalPages + ' - ' + pagination.total +
                    (isCashView ? ' cash bill(s)' : ' invoice(s)')}
              </span>
              <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={limit}
                  onChange={(event) => setLimit(parseInt(event.target.value, 10))}
                  style={{ ...toolbarInputStyle, width: 'auto' }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size + ' per page'}
                    </option>
                  ))}
                </select>
                <button
                  type='button'
                  onClick={() => setPage((value) => Math.max(value - 1, 1))}
                  disabled={pagination.page <= 1}
                  style={{ ...pagerButtonStyle, opacity: pagination.page <= 1 ? 0.5 : 1 }}
                >
                  Prev
                </button>
                <button
                  type='button'
                  onClick={() => setPage((value) => Math.min(value + 1, pagination.totalPages))}
                  disabled={pagination.page >= pagination.totalPages}
                  style={{ ...pagerButtonStyle, opacity: pagination.page >= pagination.totalPages ? 0.5 : 1 }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {view === 'parties' ? (
          <div>
            {partiesLoading ? (
              <p style={{ padding: spacing.lg, color: colors.text.secondary }}>
                Loading {config.partyTitlePlural.toLowerCase()}...
              </p>
            ) : partySummaries.length === 0 ? (
              <EmptyState
                title={config.emptyParties}
                hint={partySearch ? 'No ' + config.partyTitlePlural.toLowerCase() + ' match your search.' : config.emptyPartiesHint}
              />
            ) : (
              <div className='pd-table-wrap'>
                <table className='pd-table'>
                  <thead>
                    <tr>
                      <th>{config.partyTitle}</th>
                      <th>Contact</th>
                      <th style={{ textAlign: 'right' }}>Invoices</th>
                      <th style={{ textAlign: 'right' }}>{config.totalLabel}</th>
                      <th style={{ textAlign: 'right' }}>{config.paidLabel}</th>
                      <th style={{ textAlign: 'right' }}>{config.outstandingLabel}</th>
                      <th style={{ textAlign: 'right' }}>{config.overdueLabel}</th>
                      <th>{config.key === 'purchases' ? 'Latest Purchase' : 'Latest Billing'}</th>
                      {config.hasCreditDays ? <th>Credit Days</th> : null}
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {partySummaries.map((party) => {
                      const busy = actionPartyId === party.id;
                      return (
                        <tr key={party.id} style={{ backgroundColor: party.overdue > 0 ? '#fffbfb' : 'white' }}>
                          <td>
                            <button
                              type='button'
                              onClick={() => setPartyDetailId(party.id)}
                              style={{
                                border: 'none',
                                background: 'transparent',
                                padding: 0,
                                color: colors.primary[600],
                                fontWeight: 800,
                                cursor: 'pointer',
                                textAlign: 'left',
                                fontSize: '0.92rem',
                              }}
                            >
                              {party.name}
                            </button>
                          </td>
                          <td style={{ fontSize: '0.82rem', color: colors.text.secondary }}>
                            {party.contactPerson || '-'}
                            {party.phone ? <div>{party.phone}</div> : null}
                          </td>
                          <td style={{ textAlign: 'right' }}>{party.invoiceCount}</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(party.totalAmount)}</td>
                          <td style={{ textAlign: 'right', color: TONES.paid.color, fontWeight: 700 }}>
                            {formatCurrency(party.totalPaid)}
                          </td>
                          <td style={{ textAlign: 'right', color: TONES.unpaid.color, fontWeight: 800 }}>
                            {formatCurrency(party.outstanding)}
                          </td>
                          <td style={{ textAlign: 'right', color: TONES.overdue.color, fontWeight: 800 }}>
                            {formatCurrency(party.overdue)}
                          </td>
                          <td>{party.latestInvoiceDate ? formatDate(party.latestInvoiceDate) : '-'}</td>
                          {config.hasCreditDays ? (
                            <td style={{ fontSize: '0.82rem', color: colors.text.secondary }}>
                              {party.creditDays ? party.creditDays + ' days' : '-'}
                            </td>
                          ) : null}
                          <td style={{ textAlign: 'right' }}>
                            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              <ActionButton onClick={() => setPartyDetailId(party.id)}>View</ActionButton>
                              <ActionButton onClick={() => setPartyFormModal({ party })}>Edit</ActionButton>
                              <ActionButton onClick={() => deleteParty(party)} disabled={busy} tone='overdue'>
                                Delete
                              </ActionButton>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className='pd-pagination'>
              <span style={{ fontSize: '0.82rem', color: colors.text.secondary }}>
                {partyPagination.total === 0
                  ? 'No records'
                  : 'Page ' + partyPagination.page + ' of ' + partyPagination.totalPages + ' - ' + partyPagination.total + ' ' +
                    config.partyTitlePlural.toLowerCase()}
              </span>
              <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'center', flexWrap: 'wrap' }}>
                <select
                  value={partyLimit}
                  onChange={(event) => setPartyLimit(parseInt(event.target.value, 10))}
                  style={{ ...toolbarInputStyle, width: 'auto' }}
                >
                  {PAGE_SIZE_OPTIONS.map((size) => (
                    <option key={size} value={size}>
                      {size + ' per page'}
                    </option>
                  ))}
                </select>
                <button
                  type='button'
                  onClick={() => setPartyPage((value) => Math.max(value - 1, 1))}
                  disabled={partyPagination.page <= 1}
                  style={{ ...pagerButtonStyle, opacity: partyPagination.page <= 1 ? 0.5 : 1 }}
                >
                  Prev
                </button>
                <button
                  type='button'
                  onClick={() => setPartyPage((value) => Math.min(value + 1, partyPagination.totalPages))}
                  disabled={partyPagination.page >= partyPagination.totalPages}
                  style={{ ...pagerButtonStyle, opacity: partyPagination.page >= partyPagination.totalPages ? 0.5 : 1 }}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Add / edit invoice modal */}
      {invoiceModal ? (
        <FinanceInvoiceModal
          config={config}
          invoice={invoiceModal.invoice}
          presetParty={invoiceModal.presetParty}
          presetBillType={invoiceModal.presetBillType}
          parties={parties}
          onClose={() => setInvoiceModal(null)}
          onSaved={handleInvoiceSaved}
        />
      ) : null}

      {/* Record a part payment / instalment */}
      {paymentModal ? (
        <FinancePaymentModal
          config={config}
          invoice={paymentModal}
          onClose={() => setPaymentModal(null)}
          onSaved={handlePaymentSaved}
        />
      ) : null}

      {/* Distributor / store detail modal */}
      {partyDetailId ? (
        <FinancePartyModal
          config={config}
          partyId={partyDetailId}
          refreshToken={refreshToken}
          onClose={() => setPartyDetailId(null)}
          onChanged={handlePartyModalChanged}
          onEditParty={(party) => setPartyFormModal({ party })}
          onEditInvoice={(invoice) => setInvoiceModal({ invoice })}
          onRecordPayment={(invoice) => setPaymentModal(invoice)}
          onAddInvoice={(party) => setInvoiceModal({ invoice: null, presetParty: party })}
        />
      ) : null}

      {/* Add / edit distributor or store */}
      {partyFormModal ? (
        <FinancePartyFormModal
          config={config}
          party={partyFormModal.party}
          onClose={() => setPartyFormModal(null)}
          onSaved={handlePartySaved}
        />
      ) : null}

      {/* Success / error toast */}
      {toast ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          style={{
            position: 'fixed',
            right: spacing.lg,
            bottom: spacing.lg,
            zIndex: 80,
            maxWidth: '360px',
            padding: spacing.md,
            borderRadius: '0.75rem',
            boxShadow: shadows.lg,
            backgroundColor: toast.type === 'error' ? TONES.overdue.background : TONES.paid.background,
            border: '1px solid ' + (toast.type === 'error' ? TONES.overdue.border : TONES.paid.border),
            color: toast.type === 'error' ? TONES.overdue.color : TONES.paid.color,
            fontWeight: 700,
            fontSize: '0.88rem',
          }}
          role='status'
        >
          {toast.message}
        </motion.div>
      ) : null}
    </div>
  );
}

export default PurchasesDistribution;









